import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import logo from '../../../images/mg_logo.png';
import Icon from '../Icon';
import CloseButton from '../CloseButton';
import { ReactComponent as BackIcon } from '../../../icons/arrow-left-circle-solid.svg';

import './Modal.scss';

const Modal = ({
  isOpen,
  onClose,
  onBack = null,
  title,
  actions = null,
  sheet = null,
  bodyRef = null,
  children,
  footer = null,
  evenFooter = false,
  showCloseButton = true,
  closeOnBackdropClick = true,
  onBackdropClick = null,
  opaqueBackdrop = false,
  showLogo = false,
  className = '',
  fireworksContent = null,
}) => {
  const { t } = useTranslation();

  // Stop the page scrolling while open. Restore the old value on close, so a modal underneath stays locked.
  useEffect(() => {
    if (!isOpen) return undefined;

    const previous = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) {
      (onBackdropClick ?? onClose)();
    }
  };

  const modalClassName = classNames('Modal', className, {
    'opaque-backdrop': opaqueBackdrop,
  });

  const modalContent = (
    // View toggles auto-scroll on a double tap of the lyrics behind this, so a double click
    // anywhere in the modal must not reach it
    <div className={modalClassName} onDoubleClick={(e) => e.stopPropagation()}>
      <div className="backdrop" onClick={handleBackdropClick} />
      {fireworksContent}
      <div className="content">
        {showLogo && (
          <div className="logo-container">
            <img src={logo} alt="Migrupo" className="logo" />
          </div>
        )}
        <div className="header">
          {onBack && (
            // arrow-left-circle-solid carries its shape in the fill, so it needs Icon's `solid`
            // variant — without it App.scss's stroke paints the arrow cut-out back in
            <button className="back" onClick={onBack} aria-label={t('common.back')}>
              <Icon icon={<BackIcon />} className="solid" />
            </button>
          )}
          <h3 className="title">{title}</h3>
          {/* The X and whatever sits beside it are one row of the same round button — see
              ModalAction, which is what a caller puts in `actions`. Named for the header rather
              than `.actions`, which half the modals in the app already use for their own button
              row — and their rule, nested one level in, would reach up into this one. */}
          <div className="header-actions">
            {actions}
            {showCloseButton && <CloseButton onClick={onClose} className="close" />}
          </div>
          {sheet}
        </div>

        <div className="body" ref={bodyRef}>
          {children}
        </div>

        {footer && <div className={classNames('footer', { even: evenFooter })}>{footer}</div>}
      </div>
    </div>
  );

  // Portalled to the body so no transform, filter or overflow on whatever opened the modal can
  // trap it — View's plugin rows sit inside all three
  return ReactDOM.createPortal(modalContent, document.body);
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Given, a back arrow sits to the left of the title. For a modal that pages one view to another
  // in place rather than stacking a second dialog — see SettingsModal.
  onBack: PropTypes.func,
  title: PropTypes.string.isRequired,
  // Round icon buttons for the header, left of the X. ModalAction is the shape they take.
  actions: PropTypes.node,
  // A HeaderSheet, which drops down from under the header over the body — opened from one of `actions`
  sheet: PropTypes.node,
  // The scrolling element, handed out because a modal that pages in place has to put it back to
  // the top itself — the node survives the swap, and with it the offset the last page left behind
  bodyRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  children: PropTypes.node.isRequired,
  // Pinned action row along the bottom. Omitted, there is no row at all — most modals carry
  // their buttons inside the body instead.
  footer: PropTypes.node,
  // The footer's buttons share the row in equal pills, Cancel first
  evenFooter: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  closeOnBackdropClick: PropTypes.bool,
  // Given, a backdrop tap goes here instead of closing — for a modal that asks before losing changes
  onBackdropClick: PropTypes.func,
  opaqueBackdrop: PropTypes.bool,
  showLogo: PropTypes.bool,
  className: PropTypes.string,
  fireworksContent: PropTypes.node,
};

export default Modal;
